import {FC, useState, useEffect} from 'react'
import {Modal} from 'react-bootstrap'

type Config = {
  id?: number
  defaultCleaningTimeMinutes: number
  autoAllocationEnabled: number
  autoReleaseEnabled: number
  allowOverbooking: number
  bufferTimeMinutes: number
}

type Props = {
  show: boolean
  onClose: () => void
  onSave: (data: Omit<Config, 'id'>) => void
  config?: Config
  environmentName: string
}

export const TableConfigModal: FC<Props> = ({show, onClose, onSave, config, environmentName}) => {
  const [formData, setFormData] = useState<Omit<Config, 'id'>>({
    defaultCleaningTimeMinutes: 15,
    autoAllocationEnabled: 1,
    autoReleaseEnabled: 0,
    allowOverbooking: 0,
    bufferTimeMinutes: 30,
  })

  useEffect(() => {
    if (show && config) {
      setFormData({
        defaultCleaningTimeMinutes: config.defaultCleaningTimeMinutes,
        autoAllocationEnabled: config.autoAllocationEnabled,
        autoReleaseEnabled: config.autoReleaseEnabled,
        allowOverbooking: config.allowOverbooking,
        bufferTimeMinutes: config.bufferTimeMinutes,
      })
    }
  }, [show, config])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSave(formData)
  }

  return (
    <Modal show={show} onHide={onClose} size='lg' centered>
      <form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>
            Configurações de Gestão de Mesas
            {environmentName && <small className='text-muted ms-2'>- {environmentName}</small>}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='alert alert-info d-flex align-items-center'>
            <i className='bi bi-info-circle fs-3 me-3'></i>
            <div>
              <strong>Configurações por Ambiente</strong>
              <div className='small'>
                Estas configurações são específicas para este ambiente e afetam o comportamento
                automático do sistema de gestão de mesas.
              </div>
            </div>
          </div>

          <div className='mb-7'>
            <label className='form-label required'>Tempo de Limpeza Padrão (minutos)</label>
            <input
              type='number'
              className='form-control'
              value={formData.defaultCleaningTimeMinutes}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  defaultCleaningTimeMinutes: Number(e.target.value),
                })
              }
              min='0'
              step='5'
            />
            <div className='form-text'>
              Tempo necessário para limpar e preparar a mesa após a liberação. Mesas em limpeza não
              são alocadas automaticamente.
            </div>
          </div>

          <div className='mb-7'>
            <label className='form-label required'>Tempo de Buffer (minutos)</label>
            <input
              type='number'
              className='form-control'
              value={formData.bufferTimeMinutes}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  bufferTimeMinutes: Number(e.target.value),
                })
              }
              min='0'
              step='5'
            />
            <div className='form-text'>
              Tempo de segurança entre reservas consecutivas. Garante que haja folga entre
              atendimentos.
            </div>
          </div>

          <div className='separator my-7'></div>

          <div className='mb-7'>
            <div className='form-check form-switch'>
              <input
                className='form-check-input'
                type='checkbox'
                id='autoAllocationEnabled'
                checked={formData.autoAllocationEnabled === 1}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    autoAllocationEnabled: e.target.checked ? 1 : 0,
                  })
                }
              />
              <label className='form-check-label' htmlFor='autoAllocationEnabled'>
                <strong>Alocação Automática de Mesas</strong>
              </label>
            </div>
            <div className='form-text ms-10'>
              Quando ativado, o sistema automaticamente seleciona e aloca as melhores mesas para
              cada reserva com base na capacidade e disponibilidade. Você ainda pode fazer
              alocações manuais quando necessário.
            </div>
          </div>

          <div className='mb-7'>
            <div className='form-check form-switch'>
              <input
                className='form-check-input'
                type='checkbox'
                id='autoReleaseEnabled'
                checked={formData.autoReleaseEnabled === 1}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    autoReleaseEnabled: e.target.checked ? 1 : 0,
                  })
                }
              />
              <label className='form-check-label' htmlFor='autoReleaseEnabled'>
                <strong>Liberação Automática de Mesas</strong>
              </label>
            </div>
            <div className='form-text ms-10'>
              Quando ativado, as mesas são automaticamente liberadas (mudadas para status de
              limpeza) após a confirmação de pagamento no WebApp. Desative se preferir liberar
              manualmente.
            </div>
          </div>

          <div className='mb-7'>
            <div className='form-check form-switch'>
              <input
                className='form-check-input'
                type='checkbox'
                id='allowOverbooking'
                checked={formData.allowOverbooking === 1}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    allowOverbooking: e.target.checked ? 1 : 0,
                  })
                }
              />
              <label className='form-check-label' htmlFor='allowOverbooking'>
                <strong>Permitir Superlotação (Overbooking)</strong>
              </label>
            </div>
            <div className='form-text ms-10'>
              <span className='text-warning'>
                <i className='bi bi-exclamation-triangle me-1'></i>
                Use com cautela!
              </span>{' '}
              Quando ativado, permite aceitar reservas mesmo quando não há mesas disponíveis. Útil
              para estabelecimentos que praticam overbooking controlado.
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button type='button' className='btn btn-light' onClick={onClose}>
            Cancelar
          </button>
          <button type='submit' className='btn btn-primary'>
            Salvar Configurações
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  )
}
